import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { LayoutWithCarousel } from '@/components/Layout';
import routes from '@/constants/routes';
import type { BaseResponse, RequestActivationEmailErrorResponse } from '@/types';
import '../../authentication-screens.styles.css';
import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { AxiosResponse, isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { passwordValidation, VALIDATION_MESSAGES } from '@/constants/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { NextButton } from '@/components/buttons/Buttons';
import { Column } from '@/components/container/Container';
import PasswordInput from '@/components/form-inputs/PasswordInput';
import { CREATE_USERNAME_AND_PASSWORD_FORM } from '@/constants/forms';
import { TextHeadlineLarge } from '@/components/text/Texts';
import { useEffect } from 'react';
import { showError } from '@/modules/notification/engine/notification.library';

interface SetEmployeePassword {
	token: string;
	password: string;
}

const EmployeeSignUp = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const form = useForm({
		defaultValues: {
			password: '',
			confirmPassword: '',
		},
		resolver: yupResolver(yup.object({
			password: passwordValidation.required(VALIDATION_MESSAGES.required),
			confirmPassword: yup.string()
				.required(VALIDATION_MESSAGES.required)
				.oneOf([yup.ref('password')], 'Passwords do not match'),
		})),
		mode: "all"
	});

	const password = form.watch('password');
	useEffect(() => {
		if (password) form.trigger('confirmPassword');
	}, [password, form.trigger]);

	const employeeToken = searchParams.get('employeeToken');

	const { mutate } = useMutation({
		mutationFn: async () => {
			await axiosInstance.post<BaseResponse, AxiosResponse<BaseResponse>, SetEmployeePassword>(ENDPOINTS['/api/X4bUser/SetEmployeePassword'], {
				token: employeeToken!,
				password: form.getValues('password'),
			});
		},
		onSuccess: async () => navigate(routes['/login']),
		onError: (error) => showError(error.message ?? '', { summary: 'Error' }),
	});

	return (
		<LayoutWithCarousel>
			<Card className="sign-up-screen">
				<TextHeadlineLarge
					text="Create User name & Password"
					className="mb-8 text-inverseSurface"
				/>
				<Column className="gap-4 mb-6">
					<PasswordInput
						className="password-form-input"
						control={form.control}
						feedback
						formName={CREATE_USERNAME_AND_PASSWORD_FORM}
						id="password"
						label="Password"
					/>
					<PasswordInput
						className="password-form-input"
						control={form.control}
						formName={CREATE_USERNAME_AND_PASSWORD_FORM}
						id="confirmPassword"
						label="Confirm Password"
						placeholder=""
						needValidIcon
					/>
				</Column>
				<NextButton
					id="sign-up-next-button"
					className={`w-full ${!form.formState.isDirty ? 'disabled-button' : 'enabled-button'}`}
					disabled={!form.formState.isValid}
					label="Sign up"
					onClick={() => mutate()}
					withIcon={false}
				/>
			</Card>
		</LayoutWithCarousel>
	);
};

export default EmployeeSignUp;
